/** @jsxImportSource @emotion/react */
import { LoadingOutlined } from '@ant-design/icons'
import type { ReactNode } from 'react'
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { PRIMARY_BUTTON_CONTENT } from '@sportninja/common/constants/buttonColors'
import Text from '../Text'
import { renderIconWithColor } from './renderIconWithColor'

type ButtonProps = {
  text: string
  onClick: () => void
  disabled?: boolean
  isLoading?: boolean
  variant?: 'primary' | 'secondary' | 'tertiary' | 'quaternary' | 'danger'
  style?: any
  uppercase?: boolean
  icon?: ReactNode
}

const Button = ({
  text,
  onClick,
  disabled,
  isLoading,
  variant,
  style,
  uppercase = false,
  icon,
}: ButtonProps) => {
  // primary and quaternary are both colors.PRIMARY fills, so their content
  // follows the tenant-aware pairing.
  const isPrimaryFill = variant === 'primary' || variant === 'quaternary'

  const contentColor = disabled
    ? colors.NEUTRAL_50
    : isPrimaryFill
    ? PRIMARY_BUTTON_CONTENT.text
    : variant === 'tertiary'
    ? colors.PRIMARY
    : colors.WHITE

  const iconColor = disabled
    ? colors.NEUTRAL_50
    : isPrimaryFill
    ? PRIMARY_BUTTON_CONTENT.icon
    : variant === 'tertiary'
    ? colors.PRIMARY
    : colors.WHITE

  return (
    <button
      type='button'
      onClick={onClick}
      css={css`
        height: 40px;
        display: flex;
        padding: 8px 24px;
        justify-content: center;
        align-items: center;
        gap: 8px;
        flex: 1 0 0;
        border-radius: 8px;
        cursor: pointer;
        /* A two-word label ("Send Invoice") wrapped to two lines inside the
           40px button and overflowed it — observed live. A button says what it
           does on ONE line; it grows instead. */
        white-space: nowrap;
        ${isLoading ||
        (disabled &&
          css`
            cursor: not-allowed;
            opacity: 0.7;
          `)}
        ${variant === 'primary' &&
        css`
          background-color: ${colors.PRIMARY};
          color: ${PRIMARY_BUTTON_CONTENT.text};
          text-transform: uppercase;
        `}
        ${variant === 'secondary' &&
        css`
          background-color: transparent;
          border: 1px solid ${colors.WHITE};
          color: ${colors.WHITE};
          text-transform: uppercase;
        `}
        ${variant === 'tertiary' &&
        css`
          background-color: ${colors.WHITE};
          border: 1px solid ${colors.PRIMARY};
          color: ${colors.PRIMARY};
          text-transform: uppercase;
        `}
        ${variant === 'quaternary' &&
        css`
          background-color: ${colors.PRIMARY};
          border: 1px solid ${colors.PRIMARY};
          color: ${PRIMARY_BUTTON_CONTENT.text};
          text-transform: uppercase;
        `}
        ${variant === 'danger' &&
        css`
          background-color: ${colors.ERROR_200};
          color: ${colors.WHITE};
          text-transform: uppercase;
        `}
        ${disabled &&
        css`
          background-color: ${colors.SECONDARY_200};
        `}
        ${uppercase &&
        css`
          text-transform: uppercase;
        `}
        ${style}
      `}
      disabled={disabled || isLoading}
    >
      {isLoading ? (
        <LoadingOutlined
          css={css`
            color: ${isPrimaryFill
              ? PRIMARY_BUTTON_CONTENT.spinner
              : colors.WHITE};
            font-size: 16px;
          `}
        />
      ) : (
        <>
          {icon && (
            <span
              css={css`
                display: inline-flex;
                align-items: center;
                color: ${iconColor};
              `}
            >
              {renderIconWithColor(icon, iconColor)}
            </span>
          )}
          <Text variant='heading6' weight='semiBold' color={contentColor}>
            {text}
          </Text>
        </>
      )}
    </button>
  )
}

export default Button
